/**
 * SCRUM-365 (DS-026 S3) — Zod schemas for the per-property channel-mapping
 * HTTP boundary.
 *
 * Endpoints covered (`ChannelPropertyMappingController`):
 *   POST   /organizations/:slug/properties/:propertyId/channel/mappings
 *   POST   .../:channelId/policy
 *
 * BOUNDARY ALIGNMENT (codex F-2, mirrored from `channel-connection.ts`):
 * both schemas here are AT LEAST as strict as their corresponding domain VO
 * (`ChannelId.parse` / `CancellationPolicyRef.parse`) so a malformed body is
 * rejected with 400 before it ever reaches the VO — never an uncaught VO
 * throw surfacing as a 500.
 *
 *   - `channelId`: a closed enum of the four supported NextPax channel codes
 *     (must match `CHANNEL_CODES` in
 *     `apps/api/.../domain/property-channel-mapping/channel-id.vo.ts` — case
 *     exact, no near-miss/lowercase/trailing-separator/wildcard accepted).
 *   - `cancellationPolicyRef`: mirrors the `parseOpaqueIdentifier` structural
 *     rules (non-empty after trim, ≤128 chars = `OPAQUE_IDENTIFIER_MAX_LENGTH`,
 *     no trailing separator, not wildcard-only, no injection-shaped
 *     fragments). Validators is a leaf package (ADR 0024) — these constants
 *     are duplicated literals, not an import of domain code.
 *
 * H2 (DS-026 §5): neither schema accepts `channelPropertyId` or
 * `propertyManagerCode` — both are ALWAYS org/mapping-derived server-side.
 * `MapPropertyToChannelRequestSchema` is NOT `.strict()` so an
 * attacker-supplied extra field on the wire body is silently stripped by
 * Zod's default parse (never causes a 400, never reaches the command).
 */
import { z } from 'zod';
/** Must match `CHANNEL_CODES` (channel-id.vo.ts) — the closed NextPax channel allow-list. */
const CHANNEL_CODES = ['AIR298', 'HOM143', 'BOO142', 'EXP270'];
/** Must match `OPAQUE_IDENTIFIER_MAX_LENGTH` (opaque-identifier.ts). */
const CANCELLATION_POLICY_REF_MAX = 128;
// ---------------------------------------------------------------------------
// POST body — MapPropertyToChannelRequest
// ---------------------------------------------------------------------------
export const MapPropertyToChannelRequestSchema = z.object({
    channelId: z.enum(CHANNEL_CODES),
});
// ---------------------------------------------------------------------------
// POST body — LinkCancellationPolicyRequest
// ---------------------------------------------------------------------------
export const LinkCancellationPolicyRequestSchema = z.object({
    cancellationPolicyRef: z
        .string()
        .trim()
        .min(1)
        .max(CANCELLATION_POLICY_REF_MAX)
        .refine((v) => !v.endsWith('/') && !v.endsWith('.') && !v.endsWith('-'), 'cancellationPolicyRef must not end with a separator')
        .refine((v) => !/^\*+$/.test(v), 'cancellationPolicyRef must not be a wildcard-only value')
        .refine((v) => !['<', '>', '${', ';', '..'].some((fragment) => v.includes(fragment)), 'cancellationPolicyRef must not contain unsafe characters'),
});
// ---------------------------------------------------------------------------
// SCRUM-368 (DS-026 S6) plan P6 — activation content pre-check +
// submit/resubmit request bodies.
//
// `content` mirrors `SubmitChannelActivationCommand.content` /
// `ResubmitChannelActivationCommand.content` (application layer, P2/P3,
// already GREEN) — `{ imageCount: number; propertyName: string }`, the two
// DOCUMENTED content-readiness facts `ValidateChannelContentUseCase`
// pre-checks (min image count, name-length bounds). There is currently NO
// cross-context port exposing a property's photo COUNT, so `content` is an
// OPERATOR-SUPPLIED request-body field — content METADATA, not a
// security-sensitive identifier (H2 is unaffected: neither
// `channelPropertyId` nor `propertyManagerCode` is ever accepted here).
//
// `propertyName` mirrors `cancellationPolicyRef`'s opaque-identifier-shaped
// rules verbatim (trim/min/max/no-trailing-separator/no-wildcard-only/
// no-injection-fragment), at a 255-char ceiling (a generous display-name
// bound, distinct from the 128-char OPAQUE_IDENTIFIER_MAX_LENGTH ceiling
// used for vendor-minted ids elsewhere in this file).
// ---------------------------------------------------------------------------
const ACTIVATION_PROPERTY_NAME_MAX = 255;
export const ActivationContentSchema = z.object({
    imageCount: z.number().int().nonnegative(),
    propertyName: z
        .string()
        .trim()
        .min(1)
        .max(ACTIVATION_PROPERTY_NAME_MAX)
        .refine((v) => !v.endsWith('/') && !v.endsWith('.') && !v.endsWith('-'), 'propertyName must not end with a separator')
        .refine((v) => !/^\*+$/.test(v), 'propertyName must not be a wildcard-only value')
        .refine((v) => !['<', '>', '${', ';', '..'].some((fragment) => v.includes(fragment)), 'propertyName must not contain unsafe characters'),
});
// `content` is OPTIONAL here (SCRUM-368 codex fix — EXP270 legacy-enable
// regression): `SubmitChannelActivationUseCase` is capability-gated — the
// 369-gated EXP270 legacy branch (`onboardingReady === false`) ignores
// `content` entirely (a bodyless legacy enable), while the uniform-enable
// branch (AIR298/BOO142, `onboardingReady === true`) REQUIRES it and
// rejects a missing value with its own domain `ContentValidationFailedError`
// (422) — never a bare 400 for the capability-gated legacy channel. `.strict()`
// is retained (H2: `channelPropertyId`/`propertyManagerCode` never accepted).
export const SubmitChannelActivationRequestSchema = z
    .object({
    content: ActivationContentSchema.optional(),
})
    .strict();
// `content` stays REQUIRED here — unlike `enable`, `ResubmitChannelActivationUseCase`
// has no legacy/not-ready fallback: it capability-gates to
// `ChannelOnboardingNotReadyError` FIRST (before ever inspecting
// `enablementState` or `content`), so every mapping that reaches the use
// case body always needs content.
export const ResubmitChannelActivationRequestSchema = z
    .object({
    content: ActivationContentSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// SCRUM-368 (DS-026 S6) plan P6 — onboarding-status projection response.
//
// `OnboardingPhaseSchema` mirrors `OnboardingPhase`
// (`apps/api/.../domain/onboarding-status/derive-onboarding-phase.ts`) — a
// duplicated literal union, not a domain import (validators is a leaf
// package, ADR 0024).
//
// Disambiguation [F15]: `rawLastJobStatus` (Axis-2, per-CHANNEL remote
// vendor status) is DISTINCT from `lastExportFlowStatus` on
// `PropertyChannelMappingListItem` (Axis-1, the content-push flow status of
// the last content push) — the two never appear on the same response object.
// ---------------------------------------------------------------------------
export const OnboardingPhaseSchema = z.enum([
    'not-submitted',
    'in-progress',
    'completed',
    'failed',
    'unknown',
]);
/** Must match `EnablementState` (property-channel-mapping.aggregate.ts) — duplicated literal, leaf purity (ADR 0024). */
const ENABLEMENT_STATES = [
    'Draft',
    'PoliciesLinked',
    'RatePlanProvisioned',
    'ContentValidated',
    'Enabled',
    'Disabled',
];
export const PropertyChannelOnboardingItemSchema = z.object({
    channelId: z.enum(CHANNEL_CODES),
    enablementState: z.enum(ENABLEMENT_STATES),
    derivedPhase: OnboardingPhaseSchema,
    rawLastJobStatus: z.string().nullable(),
    lastJobError: z.string().nullable(),
    nextStep: z.string(),
});
export const PropertyOnboardingStatusResponseSchema = z.object({
    propertyId: z.string(),
    connectionStatus: z.enum(['provisioning', 'active', 'provisioning_failed', 'not-provisioned']),
    channels: z.array(PropertyChannelOnboardingItemSchema),
});
//# sourceMappingURL=property-channel-mapping.js.map